import { createSelector } from '@reduxjs/toolkit'

import { queryView, type QueryResultLike } from '@krp/client-runtime'

import type { NativeRead } from '../api/envelope'
import { formatDateTime, formatMoney, formatQuantity, normalizeGuid } from './format'
import type {
  DiscrepancyRowVm,
  PairDraft,
  PriceComparisonFilter,
  PriceRowVm,
  PricingIntentState,
  PricingOverviewVm,
  ProductPriceRow,
  ProductStockRow,
  RetailPriceDiscrepancy,
  StockRowVm,
} from './types'
import { commandRevision, mutationState, sectionOf, type MutationLike } from './viewKit'

/**
 * Именованные селекторы цен и остатков (D-061). Вход — НЕ RootState, а
 * результаты endpoint-ов и намерение фичи: binding приложения выбирает их.
 */

/** Сколько ключей сервер принимает за один запрос остатков/цен. */
export const MAX_LOOKUP_KEYS = 500
/** Серверный код ответа «личная пара видов цен ещё не задана». */
export const FILTER_NOT_SET = 'price-comparison-filter-not-set'

const NO_ROWS: readonly never[] = []
const EMPTY_PAIR: PairDraft = { first: '', second: '' }

// --- общий обзор ------------------------------------------------------------------------------------------

export interface PricingOverviewInput {
  resourceId: string
  intent: PricingIntentState
  filter: QueryResultLike<NativeRead<PriceComparisonFilter>>
  discrepancies: QueryResultLike<NativeRead<readonly RetailPriceDiscrepancy[]>>
  stock: QueryResultLike<NativeRead<readonly ProductStockRow[]>>
  prices: QueryResultLike<NativeRead<readonly ProductPriceRow[]>>
  save: MutationLike
  /**
   * Ревизия настроек базы из Platform (`configurationVersion` моего ресурса,
   * AU1b): с ней первая команда пишет сразу, не дожидаясь native-чтения. Ответ
   * самой службы свежее — он главнее; эта — только пока его нет.
   */
  resourceVersion?: string | null
}

/** Ключи из текста: по одному на строку или через запятую/пробел; повторы и пустое отбрасываются. */
export function parseKeys(text: string): { keys: readonly string[]; invalid: readonly string[] } {
  const keys: string[] = []
  const invalid: string[] = []
  for (const token of text.split(/[\s,;]+/)) {
    if (token === '') continue
    const key = normalizeGuid(token)
    if (key === null) invalid.push(token)
    else if (!keys.includes(key)) keys.push(key)
  }
  return { keys, invalid }
}

function lookupProblem(keys: readonly string[], invalid: readonly string[]): string | null {
  if (invalid.length > 0) {
    const shown = invalid.slice(0, 3).join(', ')
    return `Не ключ 1С (ожидается GUID): ${shown}${invalid.length > 3 ? ' и ещё ' + String(invalid.length - 3) : ''}`
  }
  if (keys.length > MAX_LOOKUP_KEYS) return `За один раз — не больше ${String(MAX_LOOKUP_KEYS)} товаров, указано ${String(keys.length)}`
  return null
}

function pairProblem(pair: PairDraft): string | null {
  const first = normalizeGuid(pair.first)
  const second = normalizeGuid(pair.second)
  if (pair.first.trim() === '' || pair.second.trim() === '') return 'Укажите оба вида цен'
  if (first === null || second === null) return 'Вид цен — это ключ 1С (GUID)'
  if (first === second) return 'Виды цен должны различаться'
  return null
}

export const makeSelectPricingOverviewVm = () => {
  const selectFilterQuery = (input: PricingOverviewInput) => input.filter
  const selectDiscrepancies = (input: PricingOverviewInput) => input.discrepancies
  const selectStock = (input: PricingOverviewInput) => input.stock
  const selectPrices = (input: PricingOverviewInput) => input.prices
  const selectSave = (input: PricingOverviewInput) => input.save
  const selectRid = (input: PricingOverviewInput) => input.resourceId
  const selectIntent = (input: PricingOverviewInput) => input.intent

  const selectFilterVm = createSelector(
    [selectFilterQuery, selectIntent, selectRid],
    (query, intent, resourceId): PricingOverviewVm['filter'] => {
      // «Не задан» — не ошибка, а состояние: пара ещё не выбрана.
      const notSet = query.isError && query.error !== undefined && isNotSet(query.error)
      const view = notSet ? queryView({ ...query, isError: false, isSuccess: true, data: undefined, error: undefined }, () => true) : queryView(query, () => false)
      const saved = query.data !== undefined && !query.isError ? query.data.data : null
      const draft = intent.pairs[resourceId] ?? (saved === null ? EMPTY_PAIR : { first: saved.first, second: saved.second })
      const dirty = saved === null ? draft.first !== '' || draft.second !== '' : draft.first !== saved.first || draft.second !== saved.second
      const problem = dirty ? pairProblem(draft) : null
      return {
        query: notSet ? { status: 'ready', refreshing: false, error: null } : view,
        saved: saved === null ? null : { first: saved.first, second: saved.second, updatedAtText: formatDateTime(saved.updatedAt) },
        draft,
        dirty,
        canSave: dirty && problem === null,
        problem,
      }
    },
  )

  const selectDiscrepancyVm = createSelector([selectDiscrepancies], (query) =>
    sectionOf<readonly RetailPriceDiscrepancy[], DiscrepancyRowVm>(
      query,
      (rows) =>
        rows.map((row) => ({
          productId: row.productId,
          name: row.name,
          sku: row.sku ?? '—',
          prices: row.prices.map((point) => ({ priceType: point.priceTypeKey, priceText: formatMoney(point.price) })),
        })),
      NO_ROWS,
    ),
  )
  const selectStockVm = createSelector([selectStock], (query) =>
    sectionOf<readonly ProductStockRow[], StockRowVm>(
      query,
      (rows) => rows.map((row) => ({ productId: row.productId, warehouse: row.warehouseRefKey, quantityText: formatQuantity(row.quantity) })),
      NO_ROWS,
    ),
  )
  const selectPriceVm = createSelector([selectPrices], (query) =>
    sectionOf<readonly ProductPriceRow[], PriceRowVm>(
      query,
      (rows) => rows.map((row) => ({ productId: row.productId, priceType: row.priceTypeRefKey, priceText: formatMoney(row.price) })),
      NO_ROWS,
    ),
  )

  const selectVersion = createSelector(
    [selectDiscrepancies, selectFilterQuery, selectStock, selectPrices, (input: PricingOverviewInput) => input.resourceVersion],
    (discrepancies, filter, stock, prices, resourceVersion) =>
      commandRevision(resourceVersion, discrepancies, filter, stock, prices),
  )

  return createSelector(
    [selectRid, selectIntent, selectFilterVm, selectDiscrepancyVm, selectStockVm, selectPriceVm, selectSave, selectVersion],
    (resourceId, intent, filter, discrepancies, stock, prices, save, configurationVersion): PricingOverviewVm => {
      const keysText = intent.keysText[resourceId] ?? ''
      const parsed = parseKeys(keysText)
      const problem = lookupProblem(parsed.keys, parsed.invalid)
      return {
        resourceId,
        configurationVersion,
        filter,
        comparing: intent.comparing[resourceId] ?? null,
        discrepancies,
        lookup: {
          keysText,
          keys: parsed.keys,
          problem,
          canLookup: parsed.keys.length > 0 && problem === null && configurationVersion !== null,
          stock,
          prices,
        },
        save: mutationState(save),
      }
    },
  )
}

function isNotSet(error: { code?: unknown }): boolean {
  return error.code === FILTER_NOT_SET
}
